import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import BrandWordmark from '../components/BrandWordmark';

// Shield Logo Component
const ShieldLogo = () => (
  <svg width="80" height="80" viewBox="0 0 120 120" style={{ filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.2))' }}>
    <path d="M60 10L10 30V55C10 80.7 30 104.5 60 110C90 104.5 110 80.7 110 55V30L60 10Z" fill="none" stroke="#222222" strokeWidth="5"/>
    <path d="M60 10L10 30V55C10 80.7 30 104.5 60 110V10Z" fill="#dd2c00"/> {/* Red */}
    <path d="M60 10L110 30V55C110 80.7 90 104.5 60 110V10Z" fill="#1e88e5"/> {/* Blue */}
    <path d="M10 55C10 80.7 30 104.5 60 110V55H10Z" fill="#2e7d32"/> {/* Green */}
    <path d="M60 55H110C110 80.7 90 104.5 60 110V55Z" fill="#fbc02d"/> {/* Yellow */}
    <path d="M42 60L55 75L80 45" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface InvitationDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  businessId: string;
  businessName: string;
  userExists?: boolean; // Indicates if the user already exists
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [success, setSuccess] = useState(false);

  // Determine correct store URL based on device
  const getStoreUrl = () => {
    try {
      const ua = navigator.userAgent || (navigator as any).vendor || (window as any).opera;
      if (/android/i.test(ua)) {
        return 'https://play.google.com/store/apps/details?id=com.jacobhartwell.SafetyCheckNow';
      }
      return 'https://apps.apple.com/us/app/check-now/id6745303459';
    } catch {
      // Fallback to App Store if detection fails
      return 'https://apps.apple.com/us/app/check-now/id6745303459';
    }
  };

  // Step labels
  const steps = ['Verify Invitation', 'Set Password', 'Complete'];

  useEffect(() => {
    const verifyInvitation = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!token) {
          setError('Invalid invitation link');
          return;
        }

        console.log('Verifying invitation token:', token);
        
        const response = await axios.get(`${API_BASE_URL}/invitations/verify/${token}`);

        if (response.data.success) {
          console.log('Invitation verified successfully:', response.data);
          setInvitation(response.data.invitation);
          setActiveStep(1); // Move to password step
        } else {
          setError('Invalid or expired invitation');
        }
      } catch (error: any) {
        console.error('Error verifying invitation:', error);
        if (error.response?.status === 404) {
          setError('This invitation link is invalid or has already been used');
        } else if (error.response?.status === 400 && error.response?.data?.expired) {
          setError('This invitation has expired');
        } else {
          setError('An error occurred. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    verifyInvitation();
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check password strength when password field changes
    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password: string) => {
    if (password.length < 8) {
      setPasswordStrength('weak');
      return;
    }

    // Check for complexity (at least 3 of the following: uppercase, lowercase, numbers, special chars)
    let complexity = 0;
    if (/[A-Z]/.test(password)) complexity++;
    if (/[a-z]/.test(password)) complexity++;
    if (/[0-9]/.test(password)) complexity++;
    if (/[^A-Za-z0-9]/.test(password)) complexity++;

    if (complexity < 3) {
      setPasswordStrength('weak');
    } else if (password.length < 12) {
      setPasswordStrength('medium');
    } else {
      setPasswordStrength('strong');
    }
  };

  const getPasswordHelperText = () => {
    if (!formData.password) return 'Password must be at least 8 characters long';
    
    switch (passwordStrength) {
      case 'weak':
        return 'Weak - Use at least 8 characters with uppercase, lowercase, numbers, and special characters';
      case 'medium':
        return 'Medium - Consider using a longer password for better security';
      case 'strong':
        return 'Strong password';
      default:
        return '';
    }
  };

  const getPasswordColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'error';
      case 'medium': return 'warning';
      case 'strong': return 'success';
      default: return undefined;
    }
  };

  const handleSetPassword = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate passwords
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      console.log('Setting password for token:', token);
      
      const response = await axios.post(`${API_BASE_URL}/invitations/set-password`, {
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      console.log('Password set response:', response.data);
      
      if (response.data.success) {
        setSuccess(true);
        setActiveStep(2); // Move to complete step

        // If shouldDownload is true, redirect to download page after a delay
        if (response.data.shouldDownload) {
          setTimeout(() => {
            window.open(getStoreUrl(), '_blank');
          }, 2000);
        } else {
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } else {
        setError(response.data.message || 'Failed to create account');
      }
    } catch (error: any) {
      console.error('Error setting password:', error);
      setError(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {invitation?.userExists ? 'Reset Your Password' : 'Create Your Account'}
            </Typography>
            {invitation && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1">
                  Hello <strong>{invitation.firstName} {invitation.lastName}</strong>, 
                  {invitation.userExists 
                    ? ` you've been invited to reset your password for ${invitation.businessName}.` 
                    : ` you've been invited to join ${invitation.businessName}.`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {invitation.userExists 
                    ? 'Please set a new password to access your account.' 
                    : 'Please set a password to complete your registration.'}
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  color={getPasswordColor()}
                  helperText={getPasswordHelperText()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  error={formData.password !== formData.confirmPassword && formData.confirmPassword.length > 0}
                  helperText={
                    formData.password !== formData.confirmPassword && formData.confirmPassword.length > 0
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSetPassword}
                disabled={loading || formData.password !== formData.confirmPassword || formData.password.length < 8}
                sx={{ 
                  backgroundColor: '#1e88e5',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  },
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} /> : (invitation?.userExists ? 'Reset Password' : 'Create Account')}
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="primary">
              {invitation?.userExists ? 'Password Reset Successfully!' : 'Account Created Successfully!'}
            </Typography>
            <Typography variant="body1" paragraph>
              {invitation?.userExists 
                ? 'Your password has been reset. Please download the app to continue.' 
                : 'Your account has been created. Please download the app to continue.'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.open(getStoreUrl(), '_blank')}
              sx={{ 
                mt: 2,
                backgroundColor: '#1e88e5',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
                fontWeight: 600
              }}
            >
              Download Now
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Add Google Fonts link for consistent typography */}
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Roboto:wght@400;500;700&family=Lato:wght@400;700&display=swap" rel="stylesheet" />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',  
              alignItems: 'center', 
              mb: 4
            }}
          >
            {/* Add Shield Logo */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <ShieldLogo />
            </Box>
            <Box sx={{ mb: 1 }}>
              <BrandWordmark variant="h4" component="h1" />
            </Box>
            <Typography 
              variant="h5" 
              component="h2" 
              color="primary" 
              sx={{ 
                fontFamily: "'Roboto', sans-serif", 
                fontWeight: 500
              }}
            >
              Invitation Acceptance
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Account created successfully!
            </Alert>
          )}

          {renderStepContent()}
        </Paper>
      </Container>
    </>
  );
};

export default AcceptInvitation; 